import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import useRouter from 'use-react-router';

import { useGetCompanySettings } from '@/api/companies/queries';
import { useUpdateCompanySettings } from '@/api/companies/mutations';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchListOptions } from '@/store/lists/actions';
import { selectList } from '@/store/lists/selectors';
import paths from '@/routing/routes/_paths';
import PageLoader from '@/library/components/general/PageLoader';
import EmptyPage from '@/common/components/general/EmptyPage';
import DangerousActionModal from '@/library/components/modals/DangerousActionModal';
import setup from '@/common/assets/svg/common/setup.svg';
import libraryVars from '@/library/assets/scss/abstracts/_exports.module.scss';

import Title from './Title';
import SettingsFilters from './SettingsFilters';
import UnsavedChangesBanner from './UnsavedChangesBanner';
import { CompanySettingsDraftProvider, useCompanySettingsDraft } from './draft-context';
import CrewAutomatedCredentials from './crew-automated-credentials';
import ExternalEmailIntegration from './external-email-integration';
import MGASettings from './mga';
import PMSSettings from './pms';
import OnboardSync from './onboard-sync';
import Accounting from './accounting';
import Forms from './forms';
import ManualsExport from './manuals-export';
import EvaluationsSettings from './evaluations-settings';
import ItineraryOnboard from './itinerary-onboard';
import Purchasing from './purchasing';
import CaptainReports from './captain-reports';
import Invoices from './invoices';

type CompanySettingsMatchParams = {
  id?: string;
};

type OrganizationOption = {
  id: number | string;
  name: string;
};

const CompanySettingsBody = () => {
  const dispatch = useAppDispatch();
  const { match, history } = useRouter<CompanySettingsMatchParams>();
  const companyId = match.params.id;
  const organizations = useAppSelector(selectList('organizations'));
  const { changes, hasUnsavedChanges, resetAll, clearChanges, validateAll } =
    useCompanySettingsDraft();
  const [pendingNavigation, setPendingNavigation] = useState<{
    company: OrganizationOption | null;
  } | null>(null);

  const activeCompany = useMemo(() => {
    if (!companyId) return null;

    const parsedCompanyId = parseInt(companyId, 10);
    const fromList = organizations?.options.find(
      organization => organization.id === parsedCompanyId
    );

    return fromList || { id: parsedCompanyId, name: '' };
  }, [companyId, organizations?.options]);

  useEffect(() => {
    if (!organizations?.options.length) {
      dispatch(fetchListOptions('organizations'));
    }
  }, [dispatch, organizations?.options.length]);

  const { data: companySettings, isLoading: isLoadingCompanySettings } =
    useGetCompanySettings(companyId);
  const { mutateAsync: updateCompanySettings, status: updateCompanySettingsStatus } =
    useUpdateCompanySettings(companyId || '');

  const isLoading = isLoadingCompanySettings || updateCompanySettingsStatus === 'pending';

  const navigateToCompany = (company: OrganizationOption | null) => {
    if (!company) {
      history.push(paths.company_settings);
      return;
    }

    history.push(`${paths.company_settings}/${company.id}`);
  };

  const handleCompanyChange = (company: OrganizationOption | null) => {
    if (hasUnsavedChanges) {
      setPendingNavigation({ company });
      return;
    }

    navigateToCompany(company);
  };

  const handleCancel = () => {
    resetAll();
    history.push(paths.companies);
  };

  const handleSave = async () => {
    if (!companyId || !changes.length) return;

    const invalidCategoryId = validateAll([...new Set(changes.map(change => change.categoryId))]);
    if (invalidCategoryId) return;

    try {
      await updateCompanySettings(changes.map(change => change.payload));
      clearChanges();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="cpt-4">
      <Title
        selectedCompany={activeCompany}
        onCompanyChange={handleCompanyChange}
        onCancel={handleCancel}
        onSave={handleSave}
        disableCancel={isLoading}
        disableSave={!hasUnsavedChanges || isLoading}
      />

      {!companyId ? (
        <EmptyPage
          pageIcon={setup}
          pageText="Select a company to view and edit its settings"
          css={css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 70vh;

            .empty-page__bg {
              position: absolute;
              transform: translate(-50%, 0%);
              pointer-events: none;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 0;
              width: 600px;
              height: 530px;
              color: ${libraryVars.athensGray};
            }

            .empty-page__text {
              transform: translateY(-20%);
              position: relative;
              z-index: 1;
              font-size: 24px;
              font-weight: 400;
              text-align: center;
            }
          `}
        />
      ) : (
        <div className="cmt-16">
          <SettingsFilters />
          <UnsavedChangesBanner />
          <CrewAutomatedCredentials companySettings={companySettings} />
          <Purchasing companySettings={companySettings} />
          <Forms companySettings={companySettings} />
          <CaptainReports companySettings={companySettings} />
          <PMSSettings companySettings={companySettings} />
          <Accounting companySettings={companySettings} />
          <Invoices companySettings={companySettings} />
          <MGASettings companySettings={companySettings} />
          <EvaluationsSettings companySettings={companySettings} />
          <ExternalEmailIntegration companySettings={companySettings} />
          <ItineraryOnboard companySettings={companySettings} />
          <ManualsExport companySettings={companySettings} />
          <OnboardSync companySettings={companySettings} />
        </div>
      )}

      <DangerousActionModal
        icon={null}
        onAccept={() => {
          const nextCompany = pendingNavigation?.company ?? null;
          setPendingNavigation(null);
          navigateToCompany(nextCompany);
        }}
        onCancel={() => setPendingNavigation(null)}
        isOpen={pendingNavigation !== null}
        acceptButtonText="DISCARD"
        acceptButtonBackground="primary"
      >
        <div className="d-flex flex-column align-items-center justify-content-center fw-300">
          <span className="fs-20 fw-400 mb-2">Unsaved changes</span>
          Switching companies will discard your unsaved settings changes.
        </div>
      </DangerousActionModal>
      <PageLoader isLoading={isLoading} />
    </div>
  );
};

const CompanySettings = () => {
  const { match } = useRouter<CompanySettingsMatchParams>();

  return (
    <CompanySettingsDraftProvider key={match.params.id || 'none'}>
      <CompanySettingsBody />
    </CompanySettingsDraftProvider>
  );
};

export default CompanySettings;
